import { Component, inject, OnInit, ChangeDetectionStrategy, signal, computed } from '@angular/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { MessageModule } from 'primeng/message';

import { addDays, formatCalendarDay, isValidReloadRange, reloadRangeBounds, RELOAD_RANGE_MAX_DAYS } from './reload-date-range';

/** The only periods the API accepts (anything else is a 400 RELOAD_PERIOD_INVALID). */
export const RELOAD_PERIOD = {
    YESTERDAY_AND_TODAY: 'yesterday_and_today',
    YESTERDAY: 'yesterday',
    LAST_7_DAYS: 'last_7_days',
} as const;
export type ReloadPeriod = (typeof RELOAD_PERIOD)[keyof typeof RELOAD_PERIOD];

/** Select value for the custom range; never sent to the API (`date_range` replaces `period`). */
const CUSTOM_RANGE = 'custom_range';
type PeriodChoice = ReloadPeriod | typeof CUSTOM_RANGE;

/** What the dialog closes with: exactly one of `period` / `dateRange` (`YYYY-MM-DD` calendar days). */
export type ReloadSelection = { period: ReloadPeriod } | { dateRange: [string, string] };

@Component({
    selector: 'app-refresh-modal',
    imports: [TranslateModule, FormsModule, SelectModule, ButtonModule, DatePickerModule, MessageModule],
    templateUrl: './refresh-modal.component.html',
    styleUrl: './refresh-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RefreshModalComponent implements OnInit {
    private config = inject(DynamicDialogConfig);
    private dialogRef = inject(DynamicDialogRef);
    private translate = inject(TranslateService);

    data = this.config.data as { campaign_id: string; reload_page?: boolean; begin_date?: string; end_date?: string };

    readonly period = signal<PeriodChoice | null>(RELOAD_PERIOD.YESTERDAY_AND_TODAY);
    readonly range = signal<(Date | null)[] | null>(null);
    count = 0;

    readonly isCustomRange = computed(() => this.period() === CUSTOM_RANGE);
    private readonly bounds = reloadRangeBounds(this.data.begin_date, this.data.end_date, new Date());
    readonly minDate = this.bounds.min;
    // While only the start is picked, the end cannot go past start + 30 days.
    readonly maxDate = computed(() => {
        const [start, end] = this.range() ?? [];
        if (!start || end) return this.bounds.max;
        const limit = addDays(start, RELOAD_RANGE_MAX_DAYS - 1);
        return limit < this.bounds.max ? limit : this.bounds.max;
    });
    readonly canConfirm = computed(() => (this.isCustomRange() ? isValidReloadRange(this.range(), this.bounds) : !!this.period()));

    readonly periodOptions = [
        { label: this.translate.instant('refreshModal.yesterdayAndToday'), value: RELOAD_PERIOD.YESTERDAY_AND_TODAY },
        { label: this.translate.instant('refreshModal.yesterday'), value: RELOAD_PERIOD.YESTERDAY },
        { label: this.translate.instant('refreshModal.last7Days'), value: RELOAD_PERIOD.LAST_7_DAYS },
        { label: this.translate.instant('refreshModal.customRange'), value: CUSTOM_RANGE },
    ];

    ngOnInit(): void {
        if (this.data.reload_page) {
            this.count = 3;
            const interval = setInterval(() => {
                this.count--;
                if (this.count === 0) {
                    clearInterval(interval);
                    window.location.reload();
                }
            }, 1000);
        }
    }

    close(): void {
        this.dialogRef.close();
    }

    confirmRefresh(): void {
        const range = this.range();
        if (this.isCustomRange()) {
            if (isValidReloadRange(range, this.bounds)) {
                this.dialogRef.close({ dateRange: [formatCalendarDay(range[0]), formatCalendarDay(range[1])] } satisfies ReloadSelection);
            }
            return;
        }
        const period = this.period();
        if (period && period !== CUSTOM_RANGE) this.dialogRef.close({ period } satisfies ReloadSelection);
    }
}
